import { useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import '../modules/module.css';

/** One qualifier (mirrors QualifierOption from the API). */
export interface QualifierOption {
  qualifierID: string;
  symbol: string | null;
  definition: string | null;
}

const PAGE_SIZE = 50;

/**
 * QualifierCombo — the inline multi-select grid picker for the Qualifier column.
 * Styled to match the shared Query-Panel grid combo (EddQsGridCombo / QueryCombo):
 * a column-headed grid with per-column filter inputs, a select-all checkbox,
 * zebra/highlight rows, pagination, and an OK button. Sourced from dbo.Qualifiers
 * (QualifierID · Symbol · Definition); the ticked qualifiers' Symbols fill the
 * cell (comma-separated). Legacy Result Entry opened this as a popup lookup grid.
 */
export function QualifierCombo({ value, options, onChange }: {
  value: string;
  options: QualifierOption[];
  onChange: (next: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [colFilters, setColFilters] = useState<string[]>(['', '', '']);
  const [page, setPage] = useState(1);
  const btnRef = useRef<HTMLButtonElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number; width: number }>({ top: 0, left: 0, width: 460 });

  // Current selection = the symbols already in the cell (split on comma or
  // whitespace so legacy "*  <" values still match).
  const selected = useMemo(
    () => new Set(value.split(/[,\s]+/).map(s => s.trim()).filter(Boolean)),
    [value]);

  const openPanel = () => {
    const r = btnRef.current?.getBoundingClientRect();
    if (r) setPos({ top: r.bottom + 2, left: r.left, width: Math.max(r.width, 460) });
    setColFilters(['', '', '']);
    setPage(1);
    setOpen(true);
  };

  // Column-filtered rows (QualifierID · Symbol · Definition).
  const filtered = useMemo(() => {
    const [fId, fSym, fDef] = colFilters.map(f => f.trim().toLowerCase());
    return options.filter(o =>
      (!fId || (o.qualifierID ?? '').toLowerCase().includes(fId)) &&
      (!fSym || (o.symbol ?? '').toLowerCase().includes(fSym)) &&
      (!fDef || (o.definition ?? '').toLowerCase().includes(fDef)));
  }, [options, colFilters]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageSafe = Math.min(page, totalPages);
  const pageRows = filtered.slice((pageSafe - 1) * PAGE_SIZE, pageSafe * PAGE_SIZE);
  const pageNumbers = useMemo(() => {
    const start = Math.max(1, Math.min(pageSafe - 2, totalPages - 4));
    return Array.from({ length: Math.min(5, totalPages) }, (_, i) => start + i);
  }, [pageSafe, totalPages]);

  const toggle = (sym: string) => {
    if (!sym) return;
    const next = new Set(selected);
    if (next.has(sym)) next.delete(sym); else next.add(sym);
    onChange(Array.from(next).join(', '));
  };
  // Select-all toggles every symbol on the current filtered set.
  const filteredSyms = filtered.map(o => (o.symbol ?? '').trim()).filter(Boolean);
  const allChecked = filteredSyms.length > 0 && filteredSyms.every(s => selected.has(s));
  const toggleAll = () => {
    const next = new Set(selected);
    if (allChecked) filteredSyms.forEach(s => next.delete(s));
    else filteredSyms.forEach(s => next.add(s));
    onChange(Array.from(next).join(', '));
  };

  useEffect(() => {
    if (!open) return;
    const onScroll = (e: Event) => {
      const pop = document.getElementById('qual-combo-pop');
      if (pop && pop.contains(e.target as Node)) return;
      setOpen(false);
    };
    document.addEventListener('scroll', onScroll, true);
    return () => document.removeEventListener('scroll', onScroll, true);
  }, [open]);

  const cols = ['QualifierID', 'Symbol', 'Definition'];
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1200;
  const popWidth = Math.min(Math.max(pos.width, 460), vw * 0.9);
  const popLeft = Math.max(8, Math.min(pos.left, vw - popWidth - 8));

  return (
    <>
      <button ref={btnRef} type="button" className="qs-combo" onClick={openPanel}>
        <span className={value ? 'qs-combo-val' : 'qs-combo-ph'}>{value || 'Select…'}</span>
        {value
          ? <i className="ti ti-x qs-combo-clear" onClick={e => { e.stopPropagation(); onChange(''); }} />
          : <i className="ti ti-chevron-down qs-combo-chev" />}
      </button>

      {open && createPortal(
        <>
          <div className="qs-combo-backdrop" onMouseDown={() => setOpen(false)} />
          <div id="qual-combo-pop" className="qs-combo-pop" style={{ top: pos.top, left: popLeft, width: popWidth, minWidth: popWidth }}>
            <div style={{ maxHeight: 340, overflow: 'auto' }}>
              <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12 }}>
                <thead>
                  <tr>
                    <th style={{ ...gcTh, width: 30, textAlign: 'center' }}>
                      <input type="checkbox" checked={allChecked} onChange={toggleAll} />
                    </th>
                    {cols.map(c => <th key={c} style={gcTh}>{c}</th>)}
                  </tr>
                  <tr>
                    <th style={gcTh} />
                    {cols.map((c, i) => (
                      <th key={c} style={{ ...gcTh, fontWeight: 400 }}>
                        <input value={colFilters[i] ?? ''}
                          onChange={e => setColFilters(f => { const n = [...f]; n[i] = e.target.value; return n; })}
                          style={{ width: '100%', boxSizing: 'border-box', font: 'inherit', padding: '2px 4px' }} />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pageRows.length === 0 ? (
                    <tr><td colSpan={cols.length + 1} className="qs-combo-empty">No matches</td></tr>
                  ) : pageRows.map(o => {
                    const sym = (o.symbol ?? '').trim();
                    const checked = selected.has(sym);
                    return (
                      <tr key={o.qualifierID} onClick={() => toggle(sym)}
                        style={{ cursor: 'pointer', background: checked ? 'var(--ai-orange-tint)' : undefined }}>
                        <td style={{ ...gcTd, textAlign: 'center' }}>
                          <input type="checkbox" checked={checked} onChange={() => toggle(sym)}
                            onClick={e => e.stopPropagation()} />
                        </td>
                        <td style={gcTd}>{o.qualifierID}</td>
                        <td style={{ ...gcTd, fontWeight: 600 }}>{sym}</td>
                        <td style={gcTd}>{o.definition}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="qs-combo-tools" style={{ gap: 4, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 11, color: 'var(--ai-muted, #6b7280)' }}>
                Page {pageSafe} of {totalPages} ({filtered.length})
              </span>
              <span style={{ flex: 1 }} />
              <button type="button" style={gcPageBtn} disabled={pageSafe <= 1}
                onClick={() => setPage(p => Math.max(1, p - 1))}>‹</button>
              {pageNumbers.map(n => (
                <button key={n} type="button" style={n === pageSafe ? gcPageBtnActive : gcPageBtn}
                  onClick={() => setPage(n)}>{n}</button>
              ))}
              <button type="button" style={gcPageBtn} disabled={pageSafe >= totalPages}
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}>›</button>
              <button type="button" className="qs-combo-ftr-ok" style={{ marginLeft: 6 }}
                onClick={() => setOpen(false)}>OK</button>
            </div>
          </div>
        </>,
        document.body)}
    </>
  );
}

// Grid styles — copied from the shared Query-Panel grid combo (EddReportQueryModal)
// so the Qualifier picker looks identical: grey sticky header, soft column/row
// separators, orange active-page button.
const gcTh: CSSProperties = {
  textAlign: 'left', padding: '4px 8px',
  borderBottom: '1px solid var(--ai-line, #e3e6ec)',
  borderRight: '1px solid var(--ai-line-soft, #eef0f3)',
  whiteSpace: 'nowrap', fontWeight: 600, position: 'sticky', top: 0, background: 'var(--ai-header-bg, #fafbfc)',
};
const gcTd: CSSProperties = {
  padding: '3px 8px',
  borderBottom: '1px solid var(--ai-line-soft, #eef0f3)',
  borderRight: '1px solid var(--ai-line-soft, #eef0f3)',
  verticalAlign: 'top',
};
const gcPageBtn: CSSProperties = {
  minWidth: 24, height: 24, padding: '0 6px', fontSize: 12, cursor: 'pointer',
  border: '1px solid var(--ai-line, #d9dde3)', borderRadius: 4,
  background: 'var(--ai-card, #fff)', color: 'var(--ai-ink, #1f2430)',
};
const gcPageBtnActive: CSSProperties = {
  ...gcPageBtn, background: 'var(--ai-orange, #e8670a)', color: '#fff',
  borderColor: 'var(--ai-orange, #e8670a)', fontWeight: 600,
};
